import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Redirect } from 'react-router-dom';
import Loading from 'components/commons/loading/Loading';
import { useTargetAchievementSupportContext } from 'contexts/TargetAchievementSupportContext';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { useModal } from 'hooks/useModal';
import { useCustomHeaderTitle } from 'hooks/useCustomHeaderTitle';
import { useScroll } from 'hooks/useScroll';
import { getTargetDetail, updateTarget } from 'config/api';
import {
  Mode,
  validateProps,
} from 'components/features/target-achievement-support/pages/TargetUpdateStep1';
import { useTargetSupportRouting } from 'components/features/target-achievement-support/hooks/useTargetSupportRouting';
import { getRangeMax } from 'components/features/target-achievement-support/pages/TargetUpdateStep2';
import { dateToString } from 'components/features/target-achievement-support/utils/utils';
import {
  TargetAccountItem,
  GlobalTargetProps,
  Target,
  TargetModalStatus,
  TargetType,
} from 'components/features/target-achievement-support/utils/type';
import { useBirthday } from 'components/features/target-achievement-support/utils/birthday';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import {
  AssetAmountInfoForm,
  getTotalAccountsAmount,
} from './TargetUpdateConfirmNew';
import { ChangeYieldCalculationForm } from '../ChangeYieldCalculationForm';
import { ExitSetHowToSaveModal } from '../modal/ExitSetHowToSaveModal';
import { EditConfirmeModal } from '../modal/EditConfirmeModal';
import { EditCompleteModal } from '../modal/EditCompleteModal';
import { TargetPremium } from '../../TargetPremium';
import { TargetBasicInfoForm } from '../../share/TargetBasicInfoForm';
import { NotFoundBirthday } from '../../birthday/NotFoundBirthday';

const TargetUpdateConfirmInternal = ({
  id,
  mode,
  confirmGlobalTarget,
  updateConfirmGlobalTarget,
}: {
  id: string;
  mode: Mode;
  confirmGlobalTarget: Target;
  updateConfirmGlobalTarget: (data: Target | null) => void;
}) => {
  const {
    goTargetUpdateInfo,
    goTargetPushUpdateStep1,
    goTargetPushDetail,
    goTargetReplaceDetail,
  } = useTargetSupportRouting();

  const totalAccountsAmount = getTotalAccountsAmount(confirmGlobalTarget);
  const selectedAccounts: TargetAccountItem[] =
    confirmGlobalTarget.targetAmount > totalAccountsAmount
      ? confirmGlobalTarget.accountList
      : [];

  const excludeAmount =
    confirmGlobalTarget.targetAmount > totalAccountsAmount
      ? confirmGlobalTarget.excludeAmount
      : 0;

  const rangeMax = getRangeMax(confirmGlobalTarget);

  const initialReserveAmount = confirmGlobalTarget.reserveAmount;
  // 毎月のつみたて額
  const [reserveAmount, setReserveAmount] = useState<number | null>(
    initialReserveAmount
  );

  const [validateReserveAmount, setValidateReserveAmount] = useState<
    validateProps
  >({ isValid: false });

  const [
    isShowExitModal,
    { hideModal: hideExitModal, showModal: showExitModal },
  ] = useModal();

  const [
    isShowEditConfirmeModal,
    { hideModal: hideEditConfirmeModal, showModal: showEditConfirmeModal },
  ] = useModal();
  const [
    isShowEditCompleteModal,
    { hideModal: hideEditCompleteModal, showModal: showEditCompleteModal },
  ] = useModal();

  const { setBack } = useCustomizeHeaderContext();
  useEffect(() => {
    setBack({ backFunction: showExitModal });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateGlobalTarget = () => {
    if (confirmGlobalTarget.targetAmount < totalAccountsAmount) {
      const updateTarget = {
        ...confirmGlobalTarget,
        accountList: selectedAccounts,
        excludeAmount,
      };
      updateConfirmGlobalTarget(updateTarget);
    }
  };

  const goUpdateInfo = async (): Promise<void> => {
    await updateGlobalTarget();
    goTargetUpdateInfo(id, mode);
  };

  const goStep1 = async () => {
    await updateGlobalTarget();
    goTargetPushUpdateStep1(id, mode);
  };

  const register = () => {
    showEditConfirmeModal();
    return;
  };

  const submit = async () => {
    const accountIDList = selectedAccounts.map(list => list.account);
    const updTargetData = {
      ...confirmGlobalTarget,
      accountIDList,
      excludeAmount,
      reserveAmount: reserveAmount ?? 0,
      startDate: new Date(confirmGlobalTarget.startDate),
      endDate: new Date(confirmGlobalTarget.endDate),
    };

    const ret = await updateTargetAPI(id, updTargetData);
    return ret;
  };

  const setReserveAmount_ = (data: number | null) => {
    const updateTarget = {
      ...confirmGlobalTarget,
      reserveAmount: data ?? 0,
    };
    updateConfirmGlobalTarget(updateTarget);
    setReserveAmount(data);
  };

  useScroll();

  return (
    <>
      <div className="mt-8">
        <TargetBasicInfoForm
          {...{ target: confirmGlobalTarget, update: () => goUpdateInfo() }}
        />
        <AssetAmountInfoForm
          {...{
            selectedAccounts,
            excludeAmount,
            update: () => goStep1(),
          }}
        />
        <ChangeYieldCalculationForm
          id={id}
          mode={mode}
          target={confirmGlobalTarget}
          reserveAmount={reserveAmount}
          setReserveAmount={setReserveAmount_}
          validateReserveAmount={validateReserveAmount}
          setValidateReserveAmount={setValidateReserveAmount}
          rangeMax={rangeMax}
          selectedAccounts={selectedAccounts}
          excludeAmount={excludeAmount}
        />
        <div className="flex flex-col mt-6 mb-16 mx-2">
          <button
            className={`flex flex-row justify-center items-baseline mb-4 py-2 text-base font-bold text-white bg-tradewell rounded-lg  ${
              validateReserveAmount.isValid
                ? ''
                : 'opacity-20 cursor-not-allowed'
            }`}
            onClick={register}
            disabled={!validateReserveAmount.isValid}>
            <div className="icon-rotate-ccw mr-1"></div>
            この内容で更新する
          </button>
          <button
            className="flex justify-center items-center py-2 text-base font-bold text-bluegray-700 bg-gray-50 border rounded-lg border-bluegray-400 shadow"
            onClick={showExitModal}>
            戻る
          </button>
        </div>
      </div>

      <ExitSetHowToSaveModal
        {...{
          hide: hideExitModal,
          isShow: isShowExitModal,
          onQuit: () => {
            updateConfirmGlobalTarget(null);
            goTargetReplaceDetail(id);
          },
          onContinueInput: hideExitModal,
        }}
      />
      <EditConfirmeModal
        {...{
          hide: hideEditConfirmeModal,
          isShow: isShowEditConfirmeModal,
          showNextModal: showEditCompleteModal,
          submit: submit,
        }}
      />
      <EditCompleteModal
        {...{
          hide: hideEditCompleteModal,
          isShow: isShowEditCompleteModal,
          updateConfirmGlobalTarget,
          finish: () => goTargetPushDetail(id),
        }}
      />
    </>
  );
};

export const TargetUpdateConfirmEdit = ({
  id,
  mode, // edit
}: {
  id: string;
  mode: Mode;
}) => {
  useCustomHeaderTitle('貯め方の変更');
  const { birthday } = useBirthday();
  const {
    globalTarget,
    confirmGlobalTarget,
    isFirstFlg,
    updateIsFirstFlg,
  } = useTargetAchievementSupportContext();

  const { data: target, isLoading, isRefetching } = useQuery(
    'TargetUpdateConfirmGlobalTargetList',
    () => getTargetListAPI(id),
    {
      refetchOnWindowFocus: false,
    }
  );

  if (target === null || target === undefined || isLoading || isRefetching) {
    return <Loading loading style={{ marginTop: '3rem' }} />;
  }

  if (!isFirstFlg && globalTarget === null && confirmGlobalTarget === null) {
    return <Redirect to={`/target/${id}`} />;
  } else {
    updateIsFirstFlg(false);
  }

  if (birthday === null || birthday === undefined) {
    return (
      <PremiumCheck content={<TargetPremium />}>
        <NotFoundBirthday />
      </PremiumCheck>
    );
  }

  return <TargetUpdateConfirmGetGlobalTarget {...{ id, mode, target }} />;
};

const TargetUpdateConfirmGetGlobalTarget = ({
  id,
  mode,
  target,
}: {
  id: string;
  mode: Mode;
  target: Target;
}) => {
  const {
    confirmGlobalTarget,
    updateConfirmGlobalTarget,
  } = useTargetAchievementSupportContext();

  useEffect(() => {
    const updateTarget = {
      ...target,
      startDate: dateToString(new Date(target.startDate)),
      endDate: dateToString(new Date(target.endDate)),
    };
    if (confirmGlobalTarget === null) updateConfirmGlobalTarget(updateTarget);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (confirmGlobalTarget === null || id !== confirmGlobalTarget.id) {
    return <Loading loading style={{ marginTop: '3rem' }} />;
  }

  return (
    <TargetUpdateConfirmInternal
      {...{
        id,
        mode,
        confirmGlobalTarget,
        updateConfirmGlobalTarget,
      }}
    />
  );
};

const getTargetListAPI = async (id: string) => {
  const res_ = await getTargetDetail(id);

  if (res_ === null) return null;
  const res = {
    id: res_.ID,
    title: res_.Title,
    refType: res_.RefType as TargetType,
    startDate: res_.StartDate,
    endDate: res_.EndDate,
    targetAmount: res_.TargetAmount,
    accountList: res_.AccountList.map(list => {
      return {
        account: list.Account,
        brokerName: list.BrokerName,
        available: list.Available,
        totalAssetAmount: list.TotalAssetAmount,
      };
    }),
    excludeAmount: res_.ExcludeAmount,
    reserveAmount: res_.ReserveAmount,
    assetAmount: res_.AssetAmount,
    yield: res_.Yield,
    achieved: res_.Achieved,
    status: res_.Status as TargetModalStatus,
  };

  return res as Target;
};

const updateTargetAPI = async (
  id: string,
  target: GlobalTargetProps
): Promise<{ tag: 'Success' | 'Error' }> => {
  const ret = await updateTarget({
    ID: id,
    TargetAmount: target.targetAmount,
    EndDate: dateToString(target.endDate),
    StartDate: dateToString(new Date()),
    Title: target.title,
    RefType: target.refType,
    ...(target.excludeAmount !== null &&
    target.reserveAmount !== null &&
    target.accountIDList !== null
      ? {
          AccountIDList: target.accountIDList,
          ExcludeAmount: target.excludeAmount,
          ReserveAmount: target.reserveAmount,
        }
      : {}),
    ...(target.managementType
      ? { ManagementType: target.managementType }
      : null),
  });

  if (ret.ResultCode === 0) {
    return { tag: 'Success' };
  } else {
    return { tag: 'Error' };
  }
};
